import { Cannabis, MapPin, Globe, CheckCircle, XCircle, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useAdmin } from './AdminContext';

export const AdminDispensariesTab = () => {
  const { dispensaryRegistrations, handleDispensaryApproval, deleteDispensaryRegistration } = useAdmin();

  return (
    <>
      <h2 className="text-xl font-semibold mb-4 flex items-center gap-2"><Cannabis size={20} /> Dispensary Registrations ({dispensaryRegistrations.length})</h2>
      {dispensaryRegistrations.length === 0 ? (
        <Card className="bg-card border-border">
          <CardContent className="py-12 text-center">
            <Cannabis size={48} className="mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">No dispensary registrations</h3>
            <p className="text-muted-foreground">Dispensary registration requests will appear here for approval.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {dispensaryRegistrations.map(disp => (
            <Card key={disp.id} className={`bg-card border-border ${disp.status === 'pending' ? 'ring-1 ring-green-500/30' : ''}`}>
              <CardHeader className="pb-2 space-y-2">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                  <CardTitle className="text-base sm:text-lg break-words">{disp.business_name}</CardTitle>
                  <Badge className={`self-start sm:self-auto shrink-0 ${disp.status === 'pending' ? 'bg-yellow-500/20 text-yellow-500' : disp.status === 'approved' ? 'bg-green-500/20 text-green-500' : 'bg-red-500/20 text-red-500'}`}>{disp.status}</Badge>
                </div>
                <CardDescription className="text-xs sm:text-sm break-all">{disp.owner_name || disp.contact_name} - {disp.email}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-2">
                {disp.city && (
                  <div className="flex items-center gap-1 text-xs sm:text-sm text-muted-foreground"><MapPin size={14} className="shrink-0" /><span>{disp.address ? `${disp.address}, ` : ''}{disp.city}, MN</span></div>
                )}
                {disp.phone && <p className="text-xs text-muted-foreground">Phone: {disp.phone}</p>}
                {disp.website && (
                  <a href={disp.website} target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline flex items-center gap-1"><Globe size={12} /> {disp.website}</a>
                )}
                {disp.license_number && <p className="text-xs text-green-400">License: {disp.license_number}</p>}
                <div className="flex flex-wrap gap-1">
                  {disp.delivery && <Badge variant="outline" className="text-xs">Delivery</Badge>}
                  {disp.medical && <Badge variant="outline" className="text-xs">Medical</Badge>}
                  {disp.recreational && <Badge variant="outline" className="text-xs">Recreational</Badge>}
                </div>
                {disp.description && <p className="text-xs sm:text-sm text-muted-foreground line-clamp-2">{disp.description}</p>}
                <p className="text-xs text-muted-foreground">Submitted: {new Date(disp.created_at).toLocaleDateString()}</p>
                {disp.status === 'pending' && (
                  <div className="flex flex-col sm:flex-row gap-2 pt-2">
                    <Button size="sm" variant="outline" className="flex-1" onClick={() => handleDispensaryApproval(disp.id, 'approve')}><CheckCircle size={14} className="mr-1" /> Approve</Button>
                    <Button size="sm" variant="destructive" className="flex-1" onClick={() => handleDispensaryApproval(disp.id, 'reject')}><XCircle size={14} className="mr-1" /> Reject</Button>
                  </div>
                )}
                <Button size="sm" variant="ghost" className="w-full text-destructive hover:text-destructive text-xs" onClick={() => deleteDispensaryRegistration(disp.id)}>
                  <Trash2 size={12} className="mr-1" /> Delete
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </>
  );
};
